import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/AuthContext";

const GENDER_OPTIONS = [["male", "Man"], ["female", "Vrouw"], ["undisclosed", "Zeg ik liever niet"]];
const RELATION_OPTIONS = [["partner", "Partner"], ["ouder", "Ouder"], ["broer_zus", "Broer / zus"], ["vriend_in", "Vriend / vriendin"], ["familie", "Familie"], ["anders", "Anders"]];

/* Module-scope (niet inline) — anders verliest het veld bij elke toets de focus. */
function Field({ label, children }) {
  return (
    <div>
      <label className="chip text-[#4A4A4A]">{label}</label>
      {children}
    </div>
  );
}

/* /profiel — "Maak je profiel compleet" + eigen gegevens beheren (spec fase 1).
   Vraagt alleen om velden die nog ontbreken; bestaande gegevens zijn vooringevuld. */
export default function Profile() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === false) { nav(`/login?next=${encodeURIComponent(`/profiel?next=${next}`)}`, { replace: true }); return; }
    if (!user || user === null) return;
    const ec = user.emergency_contact || {};
    setForm({
      dob: user.dob || "",
      gender: user.gender || "",
      phone: user.phone || "",
      ec_name: ec.name || "",
      ec_phone: ec.phone || "",
      ec_relation: ec.relation || "",
    });
  }, [user, nav, next]);

  if (user === null || !form) return null;

  const missing = user.profile_missing || [];
  const needs = (k) => missing.includes(k);
  const incomplete = user.profile_complete === false && missing.length > 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", {
        dob: form.dob || undefined,
        gender: form.gender || undefined,
        phone: form.phone || undefined,
        emergency_contact: { name: form.ec_name, phone: form.ec_phone, relation: form.ec_relation },
      });
      await refreshUser();
      if (data.profile_complete) {
        toast.success("Profiel compleet — mooi zo.");
        nav(next, { replace: true });
      } else {
        toast.error(`Nog niet compleet: ${(data.profile_missing_labels || []).join(", ")}`);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
    setBusy(false);
  };

  return (
    <main className="min-h-screen bg-[#F5F3F0] grain" data-testid="profile-page">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between line-b sticky top-0 z-40 bg-[#F5F3F0]/95 backdrop-blur-sm">
        <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover" data-testid="profile-logo">JOH.</Link>
        <Link to={next} className="chip text-[#4A4A4A] accent-hover inline-flex items-center gap-1" data-testid="profile-skip">
          {incomplete ? "Later invullen" : "Terug"} <ArrowUpRight size={12}/>
        </Link>
      </header>

      <section className="px-6 md:px-10 py-14 max-w-2xl mx-auto">
        <div className="chip text-[#4A4A4A]">— {incomplete ? "Verplichte gegevens" : "Mijn gegevens"}</div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-display uppercase text-4xl md:text-6xl leading-[0.9] mt-3 text-[#0A0A0A]"
          data-testid="profile-title"
        >
          {incomplete ? "Maak je profiel compleet." : "Mijn profiel."}
        </motion.h1>
        {incomplete && (
          <p className="mt-4 text-[#4A4A4A]" data-testid="profile-missing-note">
            We missen nog: <strong className="text-[#0A0A0A]">{(user.profile_missing_labels || []).join(", ")}</strong>.
            Dit vragen we maar één keer — daarna hoef je het nergens meer in te vullen.
          </p>
        )}

        <form onSubmit={submit} className="mt-10 border border-[#0A0A0A] bg-[#EAE7E2] p-6 md:p-8 space-y-6" data-testid="profile-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Geboortedatum">
              <input type="date" value={form.dob} onChange={set("dob")} required={needs("dob")}
                className="editorial-input" data-testid="profile-dob" max={new Date().toISOString().slice(0, 10)} />
            </Field>
            <Field label="Geslacht">
              <select value={form.gender} onChange={set("gender")} required={needs("gender")} className="editorial-input" data-testid="profile-gender">
                <option value="">Kies…</option>
                {GENDER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Telefoon (eigen nummer, optioneel)">
            <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+31 6 12345678" className="editorial-input" data-testid="profile-phone" />
          </Field>
          <div className="border border-[#0A0A0A]/25 p-4 space-y-5" data-testid="profile-emergency-block">
            <div className="chip text-[#4A4A4A]">— Noodcontact</div>
            <Field label="Naam noodcontact">
              <input value={form.ec_name} onChange={set("ec_name")} required={needs("emergency_name")} className="editorial-input" data-testid="profile-ec-name" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Telefoon noodcontact">
                <input type="tel" value={form.ec_phone} onChange={set("ec_phone")} required={needs("emergency_phone")} placeholder="+31 6 12345678" className="editorial-input" data-testid="profile-ec-phone" />
              </Field>
              <Field label="Relatie">
                <select value={form.ec_relation} onChange={set("ec_relation")} required={needs("emergency_relation")} className="editorial-input" data-testid="profile-ec-relation">
                  <option value="">Kies…</option>
                  {RELATION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn w-full justify-center" data-testid="profile-save">
            {busy ? "…" : (<><Check size={14}/> {incomplete ? "Profiel afronden" : "Opslaan"}</>)}
          </button>
        </form>
        <p className="chip text-[#4A4A4A] mt-4" data-testid="profile-privacy-note">
          Je noodcontact is alleen zichtbaar voor jou en het JOH-team — nooit voor andere leden.
        </p>
      </section>
    </main>
  );
}
